import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Button, Col, Input, Radio, Row, Select, Table, Tag, TreeSelect } from 'antd'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { PlainField } from '../../../components/SectionCard'
import bulkRelated from './bulkRelated.controller'
import dayjs from 'dayjs'
import __multiple from '../../../helpers/multiple_actions/__multiple'

const statusColor: any = {
    pending: 'default',
    processing: 'processing',
    done: 'success',
    failed: 'error',
}

export const BulkRelatedProducts = observer(() => {

    useEffect(() => {
        bulkRelated.getCategoryTree()
        bulkRelated.getLists()
        bulkRelated.startPolling()
        __multiple.setActions(bulkRelated.actions())

        return () => bulkRelated.stopPolling()
    }, [])

    const columns = [
        { title: i.t('relatedBulk:columnLabel'), dataIndex: 'label', key: 'label' },
        {
            title: i.t('relatedBulk:columnTarget'),
            key: 'target',
            render: (_: any, record: any) => record.target_type === 'category'
                ? i.t('relatedBulk:targetCategoryValue', { id: record.target_category_id })
                : i.t('relatedBulk:targetAll')
        },
        {
            title: i.t('relatedBulk:columnSource'),
            key: 'source',
            render: (_: any, record: any) => record.block_source === 'list'
                ? i.t('relatedBulk:sourceListValue', { id: record.custom_list_id })
                : i.t('relatedBulk:sourceCategory')
        },
        {
            title: i.t('relatedBulk:columnStatus'),
            key: 'status',
            render: (_: any, record: any) => <Tag color={statusColor[record.status] ?? 'default'}>{i.t(`relatedBulk:status.${record.status}`)}</Tag>
        },
        {
            title: i.t('relatedBulk:columnProgress'),
            key: 'progress',
            render: (_: any, record: any) => record.total > 0 ? `${record.processed} / ${record.total}` : '—'
        },
        {
            title: i.t('relatedBulk:columnDate'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: any) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
        },
    ]

    return (
        <>
            <Titles
                title={i.t('relatedBulk:title')}
                breadcrumb={[
                    { title: i.t('def:settings'), href: '/settings' },
                    { title: i.t('relatedBulk:title') },
                ]}
            />

            <Row gutter={[20, 20]}>
                <Col span={24}>
                    <PlainField label={i.t('relatedBulk:label')}>
                        <Input
                            value={bulkRelated.form.label}
                            onChange={e => bulkRelated.setForm('label', e.target.value)}
                            placeholder={i.t('relatedBulk:labelPlaceholder')}
                        />
                    </PlainField>
                </Col>

                <Col span={24}>
                    <PlainField label={i.t('relatedBulk:targetTitle')}>
                        <Radio.Group
                            options={[
                                { label: i.t('relatedBulk:targetAll'), value: 'all' },
                                { label: i.t('relatedBulk:targetCategory'), value: 'category' },
                            ]}
                            value={bulkRelated.form.target_type}
                            onChange={e => bulkRelated.setForm('target_type', e.target.value)}
                            optionType="button"
                        />
                    </PlainField>
                </Col>

                {bulkRelated.form.target_type === 'category' &&
                    <Col span={12}>
                        <PlainField label={i.t('relatedBulk:targetCategory')}>
                            <TreeSelect
                                style={{ width: '100%' }}
                                treeData={bulkRelated.tree}
                                value={bulkRelated.form.target_category_ids}
                                onChange={(e: any) => bulkRelated.setForm('target_category_ids', e)}
                                treeCheckable
                                multiple
                                showSearch={false}
                            />
                        </PlainField>
                    </Col>
                }

                <Col span={24}>
                    <PlainField label={i.t('relatedBulk:sourceTitle')}>
                        <Radio.Group
                            options={[
                                { label: i.t('relatedBulk:sourceCategory'), value: 'category' },
                                { label: i.t('relatedBulk:sourceList'), value: 'list' },
                            ]}
                            value={bulkRelated.form.block_source}
                            onChange={e => bulkRelated.setForm('block_source', e.target.value)}
                            optionType="button"
                        />
                    </PlainField>
                </Col>

                {bulkRelated.form.block_source === 'list' &&
                    <Col span={12}>
                        <PlainField label={i.t('relatedBulk:sourceList')}>
                            <Select
                                style={{ width: '100%' }}
                                value={bulkRelated.form.custom_list_id}
                                onChange={(e: any) => bulkRelated.setForm('custom_list_id', e)}
                                options={bulkRelated.lists.map((el: any) => ({ label: el.name, value: el.id }))}
                                showSearch
                                filterOption={(input, option: any) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            />
                        </PlainField>
                    </Col>
                }

                <Col span={24}>
                    <Button type="primary" loading={bulkRelated.loading} onClick={() => bulkRelated.start()}>
                        {i.t('relatedBulk:start')}
                    </Button>
                </Col>
            </Row>

            <Row style={{ marginTop: 32 }}>
                <Col span={24}>
                    <h3>{i.t('relatedBulk:historyTitle')}</h3>
                    <Table
                        columns={columns}
                        dataSource={bulkRelated.jobs}
                        rowKey="id"
                        pagination={false}
                        rowSelection={{
                            onSelect: (record, selected, selectedRows) => {
                                __multiple.set(selectedRows)
                            },
                            onSelectAll: (selected, selectedRows) => {
                                __multiple.set(selectedRows)
                            },
                            type: 'checkbox',
                            selectedRowKeys: __multiple.selected,
                            getCheckboxProps: (record: any) => ({
                                disabled: record.status === 'pending' || record.status === 'processing'
                            })
                        }}
                    />
                </Col>
            </Row>
        </>
    )
})
